import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fetchEventSource } from '@microsoft/fetch-event-source';
import useAuthStore from '../store/useAuthStore';
import useUIStore from '../store/useUIStore';
import { env } from '../config/env';

class FatalSSEError extends Error {}

export const useSSE = () => {
  const { isAuthenticated, user, setUser } = useAuthStore();
  const userId = user?.id;
  const queryClient = useQueryClient();
  const { addToast } = useUIStore();
  const ctrlRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated || !userId) {
      if (ctrlRef.current) {
        ctrlRef.current.abort();
        ctrlRef.current = null;
      }
      return;
    }

    const controller = new AbortController();
    ctrlRef.current = controller;
    const currentSession = () =>
      !controller.signal.aborted && useAuthStore.getState().user?.id === userId;
    const reconcileDrafts = async () => {
      const queryKey = ['pending-drafts', userId];
      // An initial request may have read the database before the event was committed.
      await queryClient.cancelQueries({ queryKey });
      if (currentSession()) await queryClient.invalidateQueries({ queryKey });
    };

    const handleOpen = async (response) => {
      if (!currentSession()) throw new FatalSSEError('SSE session changed');
      if (response.ok && response.headers.get('content-type')?.includes('text/event-stream')) {
        return;
      }

      if (response.status === 401) {
        setUser(null);
        throw new FatalSSEError('SSE session expired');
      }

      if (response.status >= 400 && response.status < 500) {
        throw new FatalSSEError(`SSE request rejected with status ${response.status}`);
      }

      throw new Error(`SSE connection failed with status ${response.status}`);
    };

    const handleError = (label) => (err) => {
      if (err instanceof FatalSSEError) {
        throw err;
      }
      console.error(`${label} error`, err);
    };

    // Connect to Notifications Stream
    const setupNotifications = async () => {
      try {
        await fetchEventSource(`${env.VITE_API_URL}/notifications/stream`, {
          credentials: 'include',
          signal: controller.signal,
          openWhenHidden: true,
          onopen: async (response) => {
            await handleOpen(response);
            // Reconcile drafts created while disconnected, including the initial connection race.
            void reconcileDrafts();
          },
          onclose() {
            throw new Error('Private updates stream disconnected');
          },
          onmessage(ev) {
            if (!currentSession()) return;
            try {
              const parsedData = JSON.parse(ev.data);
              if (parsedData.type === 'draft_changed') {
                void reconcileDrafts();
              }
              if (parsedData.type === 'notification') {
                addToast({
                  id: parsedData.data.id,
                  message: parsedData.data.message,
                  linkUrl: parsedData.data.linkUrl,
                });
                queryClient.invalidateQueries({ queryKey: ['notifications'] });
              }
            } catch (err) {
              console.error('Error parsing SSE notification data', err);
            }
          },
          onerror: handleError('SSE Notification'),
        });
      } catch (err) {
        if (err.name !== 'AbortError' && !(err instanceof FatalSSEError)) {
          console.error('Failed to establish SSE Notification connection', err);
        }
      }
    };

    // Connect to Global Updates Stream
    const setupUpdates = async () => {
      try {
        await fetchEventSource(`${env.VITE_API_URL}/updates/stream`, {
          credentials: 'include',
          signal: controller.signal,
          openWhenHidden: true,
          onopen: handleOpen,
          onmessage(ev) {
            if (!currentSession()) return;
            try {
              const parsedData = JSON.parse(ev.data);
              if (parsedData.type === 'update') {
                const { type, id, projectId, moduleId } = parsedData.data;

                if (type.startsWith('PROJECT_')) {
                  queryClient.invalidateQueries({ queryKey: ['projects'] });
                  queryClient.invalidateQueries({ queryKey: ['projects-sidebar'] });
                  if (id) queryClient.invalidateQueries({ queryKey: ['project', id] });
                } else if (type.startsWith('MODULE_')) {
                  if (projectId)
                    queryClient.invalidateQueries({ queryKey: ['project', projectId] });
                } else if (type.startsWith('TASK_') || type.startsWith('DEPENDENCY_')) {
                  queryClient.invalidateQueries({ queryKey: ['tasks'] });
                  queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
                  queryClient.invalidateQueries({ queryKey: ['project-summary-tasks'] });
                  if (moduleId)
                    queryClient.invalidateQueries({ queryKey: ['tasks', { moduleId }] });
                } else if (type.startsWith('TICKET_')) {
                  queryClient.invalidateQueries({ queryKey: ['tickets'] });
                  queryClient.invalidateQueries({ queryKey: ['project-tickets'] });
                  queryClient.invalidateQueries({ queryKey: ['department-tickets'] });
                  queryClient.invalidateQueries({ queryKey: ['department-dashboard'] });
                  // Refetching a deleted ticket would only 404
                  if (id && type !== 'TICKET_DELETED') {
                    queryClient.invalidateQueries({ queryKey: ['ticket', id] });
                  }
                  if (type === 'TICKET_PROMOTED' || type === 'TICKET_AUTO_TASK') {
                    queryClient.invalidateQueries({ queryKey: ['tasks'] });
                    queryClient.invalidateQueries({ queryKey: ['all-tasks'] });
                    queryClient.invalidateQueries({ queryKey: ['project-summary-tasks'] });
                    if (moduleId)
                      queryClient.invalidateQueries({ queryKey: ['tasks', { moduleId }] });
                  }
                }
              }
            } catch (err) {
              console.error('Error parsing SSE update data', err);
            }
          },
          onerror: handleError('SSE Update'),
        });
      } catch (err) {
        if (err.name !== 'AbortError' && !(err instanceof FatalSSEError)) {
          console.error('Failed to establish SSE Update connection', err);
        }
      }
    };

    setupNotifications();
    setupUpdates();

    return () => {
      controller.abort();
      ctrlRef.current = null;
    };
  }, [isAuthenticated, userId, setUser, queryClient, addToast]);
};
